import './style.scss';

import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tailwindConfig } from 'tailwind-config';

import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

import { useTypedSelector } from 'store';
import { IProject } from 'types/project';
import { Spinner } from 'ui/Spinner/Spinner';

import { ProjectListWithPairs } from '../ProjectListWithPairs/ProjectListWithPairs';
import { SearchProjectField } from '../SearchProjectField';

import { IconArchive, ListIcon } from 'assets/icons';

interface Props {
  listOpen: boolean;
  setListOpen: ({ type, state }: { type: 'active' | 'archive'; state: boolean }) => void;
  type: 'active' | 'archive';
  projects: IProject[];
  loading: boolean;
  arbitrageIds: number[];
  balancerTasksIds: number[];
  getArbitrages: () => Promise<void>;
  onSelectProject: (v: number) => void;
  onOpenProject: (v: number) => void;
  selectedProjectId: Nullable<number>;
}

const ProjectsList = ({
  listOpen,
  setListOpen,
  type,
  projects,
  loading,
  arbitrageIds,
  balancerTasksIds,
  getArbitrages,
  onSelectProject,
  selectedProjectId,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRefs = useRef<Set<number>>(new Set());
  const isMountedRef = useRef(true);

  const isSidebarOpened = useTypedSelector(store => store.ui.isSidebarOpened);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const searchText = useTypedSelector(store =>
    type === 'active' ? store.ui.searchProjectText : store.ui.searchArchiveProjectText,
  );

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(8), // 8px gap from the reference element
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift({
        padding: 8,
        limiter: { fn: ({ x, y }) => ({ x, y }) },
      }),
    ],
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const hover = useHover(context, {
    enabled: !isSidebarOpened,
    delay: {
      open: 100,
      close: 150,
    },
    handleClose: safePolygon({
      blockPointerEvents: true,
    }),
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  const onOpenProjectsList = useCallback((): void => {
    setListOpen({ type, state: !listOpen });
  }, [type, listOpen, setListOpen]);

  const searchedProjects = useMemo(() => {
    const _projects = projects ?? [];

    if (searchText === '') return _projects;

    return _projects.filter(el => {
      const searchValue = searchText.toLowerCase();

      return (
        el.name.toLowerCase().includes(searchValue) ||
        !!el.dexPairs.find(pair => pair.symbol.toLowerCase().includes(searchValue)) ||
        !!el.cexPairs.find(pair => pair.symbol.toLowerCase().includes(searchValue))
      );
    });
  }, [projects, searchText]);

  // Optimized scroll function with cleanup
  const scrollToProject = useCallback(
    (projectId: number, delay = 100) => {
      if (!isMountedRef.current) return;

      const timeoutId = window.setTimeout(() => {
        if (!isMountedRef.current) return;

        const container = isSidebarOpened
          ? document
          : document?.querySelector('.mm-projects-floating-list');

        const projectElement = container?.querySelector(`#sidebar-project-${projectId}`);

        if (projectElement) {
          projectElement.scrollIntoView({ block: 'start' });
        }

        timeoutRefs.current.delete(timeoutId);
      }, delay);

      timeoutRefs.current.add(timeoutId);
    },
    [isSidebarOpened],
  );

  // Scroll to selected project when it changes
  useEffect(() => {
    if (selectedProjectId && listOpen) {
      scrollToProject(selectedProjectId, isSidebarOpened ? 100 : 150);
    }
  }, [selectedProjectId, listOpen, isSidebarOpened, scrollToProject]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      timeoutRefs.current.forEach(timeout => window.clearTimeout(timeout));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutRefs.current.clear();
    };
  }, []);

  const isScrollable = !loading && projects.length !== 0;
  const showFloatingBlocks = !isSidebarOpened && isOpen;

  // Memoized project list content to avoid re-renders
  const projectListContent = useMemo(() => {
    if (loading) {
      return (
        <div className="mm-projects-list__spinner">
          <Spinner size="mini" />
        </div>
      );
    }

    if (projects.length === 0) {
      return (
        <span className="mm-projects-list__no-info">
          {type === 'active'
            ? isAdmin
              ? "Projects don't exist yet. Please create your first one!"
              : "You aren't invited to any project. Ask 111PG admins for it"
            : 'Archive is empty'}
        </span>
      );
    }

    if (searchedProjects.length === 0) {
      return <span className="mm-projects-list__no-info">Projects or pairs not found...</span>;
    }

    return searchedProjects.map(project => (
      <ProjectListWithPairs
        key={project.id}
        type={type}
        id={`sidebar-project-${project.id}`}
        project={project}
        onSelectProject={onSelectProject}
        selectedProjectId={selectedProjectId}
        arbitrageIds={arbitrageIds}
        balancerTasksIds={balancerTasksIds}
        getArbitrages={getArbitrages}
      />
    ));
  }, [
    loading,
    projects.length,
    searchedProjects,
    type,
    isAdmin,
    onSelectProject,
    selectedProjectId,
    arbitrageIds,
    balancerTasksIds,
    getArbitrages,
  ]);

  return (
    <>
      <div className="mm-projects-wrapper">
        <div
          ref={refs.setReference}
          className={cn('mm-project-menu-item', 'projects-item', { _active: listOpen })}
          onClick={onOpenProjectsList}
          {...getReferenceProps()}
        >
          <div className="mm-project-menu-item__left">
            <div className="mm-project-menu-item__icon">
              {type === 'active' ? (
                <ListIcon color={listOpen ? tailwindConfig.theme.colors.primary : undefined} />
              ) : (
                <IconArchive
                  color={
                    listOpen
                      ? tailwindConfig.theme.colors.primary
                      : tailwindConfig.theme.colors['gray-1']
                  }
                />
              )}
            </div>
            <AnimatePresence>
              {isSidebarOpened && (
                <motion.span
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: isSidebarOpened ? 0.25 : 0 }}
                  className="mm-project-menu-item__text"
                >
                  {type === 'active' ? 'Projects' : 'Archive'}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Floating blocks when sidebar is closed and hovering */}
        <AnimatePresence>
          {showFloatingBlocks && (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 1000 }}
                {...getFloatingProps()}
              >
                <motion.div
                  className="mm-projects-floating-container"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  {projects.length !== 0 && isAdmin && (
                    <div className="mm-projects-floating-search">
                      <SearchProjectField type={type} />
                    </div>
                  )}
                  <div className="mm-projects-floating-list">{projectListContent}</div>
                </motion.div>
              </div>
            </FloatingPortal>
          )}
        </AnimatePresence>
      </div>

      {/* Regular collapsible list when sidebar is open */}
      {isSidebarOpened && (
        <>
          {listOpen && projects.length !== 0 && isAdmin && <SearchProjectField type={type} />}
          <motion.div
            initial={{ height: 0, overflow: 'hidden' }}
            animate={{
              height: listOpen ? 'auto' : 0,
              overflow: listOpen ? (!isScrollable ? 'visible' : 'auto') : 'hidden',
            }}
            transition={{ easings: ['easeIn', 'easeInOut'] }}
            className={cn('mm-projects-list', {
              scrollable: isScrollable,
            })}
          >
            {projectListContent}
          </motion.div>
        </>
      )}
    </>
  );
};

export { ProjectsList };
